import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import {
	Box,
	DialogContent,
	LinearProgress,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
	getHmrcAvailability,
	getNewWixIntegrationError,
	getNewWixIntegrationLoader,
	getNewSquarespaceIntegrationError,
	getNewSquarespaceIntegrationLoader,
	getNewStripeIntegrationError,
	getNewStripeIntegrationLoader,
} from 'redux/reducers/settings/selectors';
import {
	INTEGRATIONS_PLATFORMS_IMG,
	INTEGRATIONS_PLATFORMS_IMG_TEXT,
	IntegrationsPlatform,
} from 'redux/reducers/integrations/constants';
import {
	getHmrcAvailabilityRequest,
	getHmrcOauthUrlRequest,
	getIntegrationsRequest,
	getSpecialSchemesRequest,
	newWixIntegrationRequest,
	newWixIntegrationSuccess,
	newSquarespaceIntegrationRequest,
	newSquarespaceIntegrationSuccess,
	newStripeIntegrationRequest,
	newStripeIntegrationSuccess,
} from 'redux/reducers/settings/reducer';
import { CircleSvg } from 'ui/CircleSvg';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { getVatReportsRequest } from 'redux/reducers/reports/reducer';
import { IMPORT_REPORT_TYPE } from 'redux/reducers/reports/constants';
import { TNewIntegrationDialogProps } from './types';
import { Step1 } from './Step1';
import { Step2Wix } from './Step2Wix';
import { Step3 } from './Step3';
import { Step4 } from './Step4';
import { Step5 } from './Step5';
import { Step2Shopify } from './Step2Shopify';
import { Step2Shopware } from './Step2Shopware';
import { Step2WooCommerce } from './Step2WooCommerce';
import { Step2HMRC } from './Step2HMRC';
import { Step3HMRC } from './Step3HMRC';
import { Step4HMRC } from './Step4HMRC';
import { Step2Squarespace } from './Step2Squarespace';
import { Step3Squarespace } from './Step3Squarespace';
import { Step5Squarespace } from './Step5Squarespace';
import { Step2Stripe } from './Step2Stripe';
import { Step3Stripe } from './Step3Stripe';
import { Step5Stripe } from './Step5Stripe';

const stepsOther = ['Select Platform', 'Instructions'];
const stepsWix = [
	'Select Platform',
	'Instructions',
	'Add Credentials',
	'Additional parameters',
	'Submit',
];
const stepsSquarespace = [
	'Select Platform',
	'Instructions',
	'Add Credentials',
	'Additional parameters',
	'Submit',
];
const stepsStripe = [
	'Select Platform',
	'Instructions',
	'Add Credentials',
	'Additional parameters',
	'Submit',
];
const stepsHmrc = [
	'Select Platform',
	'Check VAT service connection',
	'Instructions',
	'Sign in to HMRC account',
];

export const NewIntegrationDialog: FC<TNewIntegrationDialogProps> = ({ open, setClose }) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { search } = useLocation();
	const navigate = useNavigate();
	const navigateParams = queryString.parse(search);
	const newWixIntegrationIsLoading = useSelector(getNewWixIntegrationLoader);
	const newWixIntegrationError = useSelector(getNewWixIntegrationError);
	const newSquarespaceIntegrationIsLoading = useSelector(getNewSquarespaceIntegrationLoader);
	const newSquarespaceIntegrationError = useSelector(getNewSquarespaceIntegrationError);
	const newStripeIntegrationIsLoading = useSelector(getNewStripeIntegrationLoader);
	const newStripeIntegrationError = useSelector(getNewStripeIntegrationError);
	const checkAvailability = useSelector(getHmrcAvailability);

	const integrationIsLoading =
		newWixIntegrationIsLoading ||
		newSquarespaceIntegrationIsLoading ||
		newStripeIntegrationIsLoading;
	const integrationError =
		newWixIntegrationError || newSquarespaceIntegrationError || newStripeIntegrationError;

	const [activeStep, setActiveStep] = useState(0);
	const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

	const [submitted, setSubmitted] = useState(false);

	const [storeAddress, setStoreAddress] = useState<string | null>(null);
	const [accountName, setAccountName] = useState<string | null>(null);
	const [APIKey, setAPIKey] = useState<string | null>(null);
	const [keyID, setKeyID] = useState<string | null>(null);
	const [storeID, setStoreID] = useState<string | null>(null);

	const [hsCode, setHsCode] = useState<string | null>(null);

	const [platform, setPlatform] = useState<IntegrationsPlatform | null>(null);
	const [steps, setSteps] = useState(stepsOther);

	const isSubmittablePlatform =
		platform === IntegrationsPlatform.SQUARESPACE || platform === IntegrationsPlatform.STRIPE;

	const handleSubmit = () => {
		if (platform === IntegrationsPlatform.STRIPE) {
			if (!APIKey || !accountName || !selectedCountry) return;
			dispatch(
				newStripeIntegrationRequest({
					name: accountName,
					apiKey: APIKey,
					hs6Rreceived: hsCode ?? '',
					originatingCountry: selectedCountry,
					locationWarehouseCountry: selectedCountry,
					sellerRegistrationCountry: selectedCountry,
				}),
			);
			setSubmitted(true);
			return;
		}

		if (platform === IntegrationsPlatform.SQUARESPACE) {
			if (!APIKey || !storeAddress || !selectedCountry) return;
			dispatch(
				newSquarespaceIntegrationRequest({
					name: storeAddress,
					domain: storeAddress,
					apiKey: APIKey,
					hs6Rreceived: hsCode ?? '',
					originatingCountry: selectedCountry,
					locationWarehouseCountry: selectedCountry,
					sellerRegistrationCountry: selectedCountry,
				}),
			);
			setSubmitted(true);
			return;
		}

		if (!platform || !APIKey || !keyID || !storeID || !storeAddress || !selectedCountry) return;
		const formData = {
			name: storeAddress,
			domain: storeAddress,
			siteId: storeID,
			accountId: keyID,
			secretKey: APIKey,
			hs6Rreceived: hsCode,
			originatingCountry: selectedCountry,
			locationWarehouseCountry: selectedCountry,
			sellerRegistrationCountry: selectedCountry,
		};

		dispatch(newWixIntegrationRequest(formData));
		setSubmitted(true);
	};

	const handleEraseForm = () => {
		setSelectedCountry(null);
		setStoreAddress(null);
		setAccountName(null);
		setAPIKey(null);
		setKeyID(null);
		setStoreID(null);
		setHsCode(null);
		setPlatform(null);
		dispatch(newWixIntegrationSuccess());
		dispatch(newSquarespaceIntegrationSuccess());
		dispatch(newStripeIntegrationSuccess());
	};

	const handlePlatformChange = (type: IntegrationsPlatform) => {
		handleEraseForm();
		setPlatform(type);
		// if (type === IntegrationsPlatform.WIX) setSteps(stepsWix);
		if (type === IntegrationsPlatform.SQUARESPACE) setSteps(stepsSquarespace);
		else if (type === IntegrationsPlatform.STRIPE) setSteps(stepsStripe);
		else if (type === IntegrationsPlatform.HMRC) setSteps(stepsHmrc);
		else setSteps(stepsOther);
	};

	const handleClose = () => {
		setClose();
		setActiveStep(0);
		handleEraseForm();
		// dispatch(getIntegrationsRequest());
		// navigate(
		// 	{ pathname: location.pathname, search: '' },
		// 	{
		// 		replace: false,
		// 	},
		// );
		// window.location.reload();
	};

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		setSubmitted(false);
		if (activeStep >= steps.length - 1) {
			dispatch(newWixIntegrationSuccess());
			dispatch(newSquarespaceIntegrationSuccess());
			dispatch(newStripeIntegrationSuccess());
		}
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
		navigate(
			{ pathname: location.pathname, search: '' },
			{
				replace: false,
			},
		);
	};

	useEffect(() => {
		if (submitted && !integrationIsLoading && !integrationError) {
			setClose();
			setActiveStep(0);
			handleEraseForm();
			setSubmitted(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [integrationIsLoading]);

	useEffect(() => {
		dispatch(getHmrcOauthUrlRequest());
		dispatch(getSpecialSchemesRequest());
		dispatch(getHmrcAvailabilityRequest());
		dispatch(
			getVatReportsRequest({
				reportType: IMPORT_REPORT_TYPE,
			}),
		);
	}, [dispatch]);

	return (
		<Dialog
			open={open}
			// onClose={setClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			sx={{
				'.MuiDialog-paper': {
					width: '100%',
					maxWidth: '800px',
					minHeight: '500px',
					borderRadius: '12px',
					margin: { xs: 0, sm: 'auto' },
				},
			}}
		>
			<DialogTitle
				color="primary"
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<span>Create New Integration</span>
				{platform && activeStep !== 0 && (
					<Box>
						<img
							src={INTEGRATIONS_PLATFORMS_IMG[platform]}
							alt=""
							style={{
								height: '25px',
							}}
						/>
						<span
							style={{
								fontSize: '0.875rem',
								fontWeight: 500,
								marginLeft: '10px',
							}}
						>
							{INTEGRATIONS_PLATFORMS_IMG_TEXT[platform]}
						</span>
					</Box>
				)}
			</DialogTitle>
			<DialogContent>
				<Box>
					<Box
						sx={{
							margin: '10px 0 20px 0',
							p: 0,
							display: { xs: 'none', sm: 'flex' },
							// width: '800px',
							// overflowX: 'hidden',
						}}
					>
						<Stepper
							activeStep={activeStep}
							sx={{
								p: 0,
								m: 0,
								// display: { xs: 'none', sm: 'flex' },
								// width: '800px',
								// overflowX: 'hidden',
							}}
						>
							{steps.map((label, index) => {
								const stepProps: { completed?: boolean } = {};
								const labelProps: {
									optional?: React.ReactNode;
								} = {};
								// if (platform !== IntegrationsPlatform.WIX && index > 1) return null;
								return (
									<Step key={label} {...stepProps}>
										<StepLabel {...labelProps}>{label}</StepLabel>
									</Step>
								);
							})}
						</Stepper>
					</Box>
					<Box sx={{ display: { sm: 'none', xs: 'flex' }, gap: 1, m: '8px 0px 16px 0px' }}>
						<CircleSvg num={activeStep + 1} active />{' '}
						<Typography
							sx={{
								color: 'rgba(0, 0, 0, 0.87)',
								fontWeight: '500',
								fontSize: '0.875rem',
							}}
						>
							{steps[activeStep]}
						</Typography>
					</Box>
					{activeStep === 0 && (
						<Step1 platform={platform} handlePlatformChange={handlePlatformChange} />
					)}
					{activeStep === 1 && platform === IntegrationsPlatform.WIX && <Step2Wix />}
					{activeStep === 1 && platform === IntegrationsPlatform.SHOPIFY && <Step2Shopify />}
					{activeStep === 1 && platform === IntegrationsPlatform.SHOPWARE && <Step2Shopware />}
					{activeStep === 1 && platform === IntegrationsPlatform.WOOCOMMERCE && (
						<Step2WooCommerce />
					)}
					{activeStep === 1 && platform === IntegrationsPlatform.SQUARESPACE && (
						<Step2Squarespace />
					)}
					{activeStep === 1 && platform === IntegrationsPlatform.STRIPE && <Step2Stripe />}
					{activeStep === 1 && platform === IntegrationsPlatform.HMRC && <Step2HMRC />}
					{activeStep === 2 && platform === IntegrationsPlatform.HMRC && <Step3HMRC />}
					{activeStep === 3 && platform === IntegrationsPlatform.HMRC && <Step4HMRC />}

					{activeStep === 2 && platform === IntegrationsPlatform.SQUARESPACE && (
						<Step3Squarespace
							APIKey={APIKey}
							setAPIKey={setAPIKey}
							storeAddress={storeAddress}
							setStoreAddress={setStoreAddress}
						/>
					)}
					{activeStep === 3 && platform === IntegrationsPlatform.SQUARESPACE && (
						<Step4
							hsCode={hsCode}
							setHsCode={setHsCode}
							selectedCountry={selectedCountry}
							setSelectedCountry={setSelectedCountry}
						/>
					)}
					{activeStep === 4 &&
						platform === IntegrationsPlatform.SQUARESPACE &&
						APIKey &&
						storeAddress &&
						selectedCountry && (
							<Step5Squarespace
								platform={platform}
								APIKey={APIKey}
								storeAddress={storeAddress}
								hsCode={hsCode}
								selectedCountry={selectedCountry}
							/>
						)}

					{activeStep === 2 && platform === IntegrationsPlatform.STRIPE && (
						<Step3Stripe
							APIKey={APIKey}
							setAPIKey={setAPIKey}
							accountName={accountName}
							setAccountName={setAccountName}
						/>
					)}
					{activeStep === 3 && platform === IntegrationsPlatform.STRIPE && (
						<Step4
							hsCode={hsCode}
							setHsCode={setHsCode}
							selectedCountry={selectedCountry}
							setSelectedCountry={setSelectedCountry}
						/>
					)}
					{activeStep === 4 &&
						platform === IntegrationsPlatform.STRIPE &&
						APIKey &&
						accountName &&
						selectedCountry && (
							<Step5Stripe
								platform={platform}
								APIKey={APIKey}
								accountName={accountName}
								hsCode={hsCode}
								selectedCountry={selectedCountry}
							/>
						)}

					{activeStep === 2 && platform === IntegrationsPlatform.WIX && (
						<Step3
							storeID={storeID}
							setStoreID={setStoreID}
							keyID={keyID}
							setKeyID={setKeyID}
							APIKey={APIKey}
							setAPIKey={setAPIKey}
							storeAddress={storeAddress}
							setStoreAddress={setStoreAddress}
							platform={platform}
						/>
					)}
					{activeStep === 3 && platform === IntegrationsPlatform.WIX && (
						<Step4
							hsCode={hsCode}
							setHsCode={setHsCode}
							selectedCountry={selectedCountry}
							setSelectedCountry={setSelectedCountry}
						/>
					)}
					{activeStep === 4 &&
						platform === IntegrationsPlatform.WIX &&
						APIKey &&
						keyID &&
						storeID &&
						storeAddress &&
						// hsCode &&
						selectedCountry && (
							<Step5
								platform={platform}
								APIKey={APIKey}
								keyID={keyID}
								storeID={storeID}
								storeAddress={storeAddress}
								hsCode={hsCode}
								selectedCountry={selectedCountry}
							/>
						)}
					{integrationIsLoading && (
						<Box sx={{ width: '100%', mt: '10px' }}>
							<LinearProgress />
						</Box>
					)}
					{submitted && !integrationIsLoading && integrationError && (
						<Box sx={{ mt: '10px', color: 'red' }}>{integrationError}</Box>
					)}
				</Box>
			</DialogContent>

			<DialogActions>
				{(isSubmittablePlatform || activeStep < steps.length - 1) && (
					<Button
						onClick={() => {
							setSubmitted(false);
							handleClose();
						}}
						disabled={integrationIsLoading}
					>
						Cancel
					</Button>
				)}
				{activeStep !== 0 && activeStep < steps.length && (
					<Button onClick={handleBack} disabled={integrationIsLoading}>
						Back
					</Button>
				)}
				{isSubmittablePlatform && activeStep === steps.length - 1 ? (
					<Button onClick={handleSubmit} disabled={integrationIsLoading || submitted}>
						Submit
					</Button>
				) : activeStep === steps.length - 1 ? (
					<Button
						autoFocus
						onClick={() => {
							setSubmitted(false);
							handleClose();
						}}
					>
						Close
					</Button>
				) : (
					<Button
						onClick={handleNext}
						autoFocus
						disabled={
							(!platform && activeStep === 0) ||
							(activeStep === 1 &&
								platform === IntegrationsPlatform.HMRC &&
								!checkAvailability?.isAvailable) ||
							(activeStep === 2 &&
								((platform === IntegrationsPlatform.WIX &&
									(!APIKey || !keyID || !storeID || !storeAddress)) ||
									(platform === IntegrationsPlatform.SQUARESPACE && (!APIKey || !storeAddress)) ||
									(platform === IntegrationsPlatform.STRIPE && (!APIKey || !accountName)) ||
									(platform === IntegrationsPlatform.HMRC && !checkAvailability?.isAvailable))) ||
							(activeStep === 3 && isSubmittablePlatform && !selectedCountry)
						}
					>
						Next
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
